import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import type {
  AutoAssignStrategy,
  AutoTransitionRule,
  IncidentCreationConfig,
  IncidentTitleTemplate,
  NormalizationConfig,
} from '../../types/pipelineConfig';
import { apiClient } from '../../services/api';
import type { Team } from '../../types/index';
import { useAuth } from '../../contexts/AuthContext';
import { StrategySelector } from './StrategySelector';
import type { StrategyOption } from './StrategySelector';
import { AvailableFieldsPane } from './QuickFieldInserter';
import { StepConfigHeader } from './StepConfigHeader';
import {
  getNormalizedTargetFieldOptions,
  getNormalizedTemplateTokenFields,
} from './normalization/normalizedTargetFields';
import { getDefaultIncidentCreationConfig } from '../../utils/pipelineDefaults';

interface IncidentCreationConfigEditorProps {
  config?: IncidentCreationConfig;
  onChange: (config: IncidentCreationConfig) => void;
  endpointType: string | number;
  disabled?: boolean;
  tenantId?: string;
  normalizationConfig?: NormalizationConfig | null;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

const TITLE_OPTIONS: StrategyOption<IncidentTitleTemplate>[] = [
  { value: 'alertTitle', label: 'Alert Title', icon: '📝', description: 'Uses the alert rule name as the incident title.' },
  { value: 'resourceAndMetric', label: 'Resource + Metric', icon: '📊', description: 'Combines the alert rule with the resource identifier.' },
  { value: 'custom', label: 'Custom', icon: '⚙️', description: 'Use a custom template with placeholders: {{alertRule}}, {{resource}}, {{source}}, {{monitoringService}}, {{normalized.fieldName}}.' },
];

const ASSIGN_OPTIONS: StrategyOption<AutoAssignStrategy>[] = [
  { value: 'none', label: 'None', description: 'Do not auto-assign.' },
  { value: 'roundRobin', label: 'Round Robin', description: 'Rotate through responders.' },
  { value: 'leastBusy', label: 'Least Busy', description: 'Assign to least busy responder.' },
  { value: 'skillBased', label: 'Skill Based', description: 'Assign by skill tags.' },
  { value: 'onCall', label: 'On-Call', description: 'Assign current on-call responder.' },
  { value: 'teamOnly', label: 'Team Only', description: 'Assign to a team without selecting an individual responder.' },
];

const RESOLVE_REASON_OPTIONS = [
  'SolutionImplemented',
  'SolutionProvided',
  'KnownError',
  'Duplicate',
  'NoCallerResponse',
] as const;

type ResolveReasonOption = {
  value: string;
  label: string;
};

function formatResolveReasonLabel(reason: string): string {
  return reason
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
}

const AUTO_RESOLVE_FIELD_OPTIONS = [
  { value: 'monitorCondition', label: 'Monitor Condition', description: 'Raw value from source (e.g., Azure: "Fired"/"Resolved")' },
] as const;

function FieldLabel({ label, helper, htmlFor }: { label: string; helper?: string; htmlFor?: string }) {
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {helper && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
      )}
    </div>
  );
}

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="pb-2 border-b border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
      {description && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );
}

function TemplateFunctionsReference() {
  return (
    <details className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300">
      <summary className="cursor-pointer font-medium text-gray-800 dark:text-gray-200">
        📖 Template Functions Reference
      </summary>
      <div className="mt-2 space-y-2">
        <p>Use <code>{'{{functionName(fieldExpression)}}'}</code> to apply formatting:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li><code>{'{{json(normalized.condition)}}'}</code> — Pretty-print JSON with indentation</li>
          <li><code>{'{{uppercase(severity)}}'}</code> — Convert to UPPERCASE</li>
          <li><code>{'{{lowercase(fieldName)}}'}</code> — Convert to lowercase</li>
          <li><code>{'{{trim(fieldName)}}'}</code> — Remove leading/trailing whitespace</li>
          <li><code>{'{{date(firedAt)}}'}</code> — Format as &quot;2024-01-15 14:30:00 UTC&quot;</li>
          <li><code>{'{{truncate(description)}}'}</code> — First 200 characters</li>
          <li><code>{'{{base64decode(fieldName)}}'}</code> — Decode base64 encoded values</li>
          <li><code>{'{{urlencode(fieldName)}}'}</code> — URL-encode special characters</li>
          <li><code>{'{{htmlencode(fieldName)}}'}</code> — HTML-encode special characters</li>
        </ul>
      </div>
    </details>
  );
}

function createRuleId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

export function IncidentCreationConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  disabled = false,
  tenantId,
  normalizationConfig,
  isSystemDefault = false,
  hideToggle,
}: IncidentCreationConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultIncidentCreationConfig(endpointType), [endpointType]);
  const config = useMemo<IncidentCreationConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    autoResolveRules: rawConfig?.autoResolveRules ?? defaultConfig.autoResolveRules ?? [],
    autoReopenRules: rawConfig?.autoReopenRules ?? defaultConfig.autoReopenRules ?? [],
    skipCreationForStates: rawConfig?.skipCreationForStates ?? defaultConfig.skipCreationForStates ?? [],
  }), [defaultConfig, rawConfig]);
  const { user } = useAuth();
  const isStepEnabled = (config as IncidentCreationConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const autoResolveRules = useMemo(() => config.autoResolveRules ?? [], [config.autoResolveRules]);
  const autoReopenRules = useMemo(() => config.autoReopenRules ?? [], [config.autoReopenRules]);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const templateCustomFields = useMemo(() => {
    const uniqueFields = new Set<string>();
    for (const field of getNormalizedTemplateTokenFields(normalizationConfig)) {
      const value = field.trim();
      if (!value) continue;
      uniqueFields.add(value);
    }
    return Array.from(uniqueFields);
  }, [normalizationConfig]);

  const normalizedTargetOptions = useMemo(
    () => getNormalizedTargetFieldOptions(normalizationConfig),
    [normalizationConfig],
  );

  const autoTransitionFieldOptions = useMemo(() => {
    return [
      ...AUTO_RESOLVE_FIELD_OPTIONS,
      ...normalizedTargetOptions.map((option) => ({
        value: `normalized.${option.value}`,
        label: `Normalized ${option.label}`,
        description: 'Value from normalization step',
      })),
    ];
  }, [normalizedTargetOptions]);

  const knownFields = useMemo(
    () => new Set<string>(autoTransitionFieldOptions.map((option) => option.value)),
    [autoTransitionFieldOptions],
  );

  const isKnownField = useCallback(
    (field: string): boolean => knownFields.has(field),
    [knownFields],
  );

  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsFetched, setTeamsFetched] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [resolveReasons, setResolveReasons] = useState<ResolveReasonOption[]>(
    () => RESOLVE_REASON_OPTIONS.map((reason) => ({
      value: reason,
      label: formatResolveReasonLabel(reason),
    })),
  );
  const teamsLoading = !isSystemDefault && !teamsFetched;
  const systemResolveReasons = useMemo(
    () =>
      RESOLVE_REASON_OPTIONS.map((reason) => ({
        value: reason,
        label: formatResolveReasonLabel(reason),
      })),
    [],
  );
  const effectiveResolveReasons = isSystemDefault ? systemResolveReasons : resolveReasons;

  useEffect(() => {
    if (isSystemDefault) return;

    const tid = tenantId ?? user?.tenantId;
    if (!tid) return;
    let cancelled = false;
    apiClient.getTeams(tid)
      .then((data) => { if (!cancelled) setTeams(data); })
      .catch(() => { if (!cancelled) setTeams([]); })
      .finally(() => { if (!cancelled) setTeamsFetched(true); });
    return () => { cancelled = true; };
  }, [isSystemDefault, tenantId, user?.tenantId]);

  useEffect(() => {
    if (isSystemDefault) return;

    const tid = tenantId ?? user?.tenantId;
    if (!tid) return;

    let cancelled = false;
    apiClient.getTenantReasonConfig(tid)
      .then((data) => {
        if (cancelled) return;

        const tenantResolveReasons = (data.resolveReasons ?? [])
          .filter((reason) => Boolean(reason?.value))
          .map((reason) => ({
            value: reason.value,
            label: reason.label?.trim() || formatResolveReasonLabel(reason.value),
          }));

        if (tenantResolveReasons.length > 0) {
          setResolveReasons(tenantResolveReasons);
        }
      })
      .catch(() => {
        // Keep initial fallback reasons
      });

    return () => { cancelled = true; };
  }, [isSystemDefault, tenantId, user?.tenantId]);

  const updateConfig = useCallback((patch: Partial<IncidentCreationConfig>) => {
    onChange({ ...config, ...patch });
  }, [config, onChange]);

  const handleDragOver = useCallback((event: React.DragEvent, target: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragOverTarget(target);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverTarget(null);
  }, []);

  const handleDropOnTitle = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const text = event.dataTransfer.getData('text/plain');
    if (!text.startsWith('{{')) return;
    const current = config.customTitleTemplate ?? '';
    updateConfig({ customTitleTemplate: current + text });
  }, [config.customTitleTemplate, updateConfig]);

  const handleDropOnDescription = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const text = event.dataTransfer.getData('text/plain');
    if (!text.startsWith('{{')) return;

    const textarea = descriptionTextareaRef.current;
    if (textarea) {
      const current = config.descriptionTemplate ?? '';
      const start = textarea.selectionStart ?? current.length;
      const end = textarea.selectionEnd ?? start;
      const newValue = current.slice(0, start) + text + current.slice(end);
      updateConfig({ descriptionTemplate: newValue });
      requestAnimationFrame(() => {
        const cursorPosition = start + text.length;
        textarea.selectionStart = cursorPosition;
        textarea.selectionEnd = cursorPosition;
        textarea.focus();
      });
      return;
    }

    updateConfig({ descriptionTemplate: (config.descriptionTemplate ?? '') + text });
  }, [config.descriptionTemplate, updateConfig]);

  const handleDropOnResolveNote = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const text = event.dataTransfer.getData('text/plain');
    if (!text.startsWith('{{')) return;
    updateConfig({ resolveNoteTemplate: (config.resolveNoteTemplate ?? '') + text });
  }, [config.resolveNoteTemplate, updateConfig]);

  const toggleStepEnabled = useCallback((enabled: boolean) => {
    onChange({ ...config, isEnabled: enabled } as IncidentCreationConfig & { isEnabled: boolean });
  }, [config, onChange]);

  const updateAutoResolveRule = useCallback((ruleId: string, patch: Partial<AutoTransitionRule>) => {
    updateConfig({
      autoResolveRules: autoResolveRules.map((rule) => (
        rule.id === ruleId
          ? { ...rule, ...patch }
          : rule
      )),
    });
  }, [autoResolveRules, updateConfig]);

  const addAutoResolveRule = useCallback(() => {
    const ruleId = createRuleId('resolve');
    updateConfig({
      autoResolveRules: [
        ...autoResolveRules,
        {
          id: ruleId,
          name: 'New Resolve Rule',
          enabled: true,
          field: '',
          value: '',
          matchStrategy: 'exact',
          resolutionNotes: '',
        },
      ],
    });
  }, [autoResolveRules, updateConfig]);

  const deleteAutoResolveRule = useCallback((ruleId: string) => {
    updateConfig({
      autoResolveRules: autoResolveRules.filter((rule) => rule.id !== ruleId),
    });
  }, [autoResolveRules, updateConfig]);

  const updateAutoReopenRule = useCallback((ruleId: string, patch: Partial<AutoTransitionRule>) => {
    updateConfig({
      autoReopenRules: autoReopenRules.map((rule) => (
        rule.id === ruleId
          ? { ...rule, ...patch }
          : rule
      )),
    });
  }, [autoReopenRules, updateConfig]);

  const addAutoReopenRule = useCallback(() => {
    const ruleId = createRuleId('reopen');
    updateConfig({
      autoReopenRules: [
        ...autoReopenRules,
        {
          id: ruleId,
          name: 'New Reopen Rule',
          enabled: true,
          field: '',
          value: '',
          matchStrategy: 'exact',
        },
      ],
    });
  }, [autoReopenRules, updateConfig]);

  const deleteAutoReopenRule = useCallback((ruleId: string) => {
    updateConfig({
      autoReopenRules: autoReopenRules.filter((rule) => rule.id !== ruleId),
    });
  }, [autoReopenRules, updateConfig]);

  return (
    <div className="flex gap-6">
      <div className="flex-1 min-w-0 space-y-6">
      <StepConfigHeader
        title="Incident Creation"
        isEnabled={isStepEnabled}
        onToggleEnabled={toggleStepEnabled}
        isSystemDefault={isSystemDefault}
        disabled={disabled}
        hideToggle={hideToggle}
      />
      <div className={isStepEnabled ? 'space-y-6' : 'space-y-6 opacity-50 pointer-events-none'}>

        <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label="Incident title and description">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>📝 Title and Description</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.titleTemplate}</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
          <SectionHeader title="Title and Description" />
        <div>
          <FieldLabel label="Title Template" helper="The title template controls how the incident title is generated from the alert data." />
          <StrategySelector
            options={TITLE_OPTIONS}
            value={config.titleTemplate}
            onChange={(titleTemplate) => updateConfig({ titleTemplate })}
            disabled={disabled}
          />
        </div>
        {config.titleTemplate === 'custom' && (
          <div>
            <FieldLabel label="Custom Title Template" helper="Use placeholders: {{alertRule}}, {{resource}}, {{source}}, {{monitoringService}}, {{normalized.fieldName}}" />
            <input
              ref={titleInputRef}
              className={`input ${dragOverTarget === 'title' ? 'ring-2 ring-blue-400' : ''}`}
              value={config.customTitleTemplate ?? ''}
              onChange={(e) => updateConfig({ customTitleTemplate: e.target.value })}
              onDragOver={(event) => handleDragOver(event, 'title')}
              onDragLeave={handleDragLeave}
              onDrop={(event) => {
                handleDropOnTitle(event);
                setDragOverTarget(null);
              }}
              disabled={disabled}
              placeholder="Custom title template"
            />
          </div>
        )}
        <div>
          <FieldLabel label="Description Template (optional)" helper="Custom template for the incident description. Leave empty to use the default format. Supports the same placeholders as the title template." />
          <textarea
            ref={descriptionTextareaRef}
            className={`input min-h-[240px] ${dragOverTarget === 'description' ? 'ring-2 ring-blue-400' : ''}`}
            value={config.descriptionTemplate ?? ''}
            onChange={(e) => updateConfig({ descriptionTemplate: e.target.value })}
            onDragOver={(event) => handleDragOver(event, 'description')}
            onDragLeave={handleDragLeave}
            onDrop={(event) => {
              handleDropOnDescription(event);
              setDragOverTarget(null);
            }}
            disabled={disabled}
            placeholder="Description template"
          />
          <TemplateFunctionsReference />
        </div>
        </div>
      </details>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>👤 Assignment</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.autoAssignStrategy}</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
          <SectionHeader title="Assignment" />
        <div>
          <FieldLabel label="Auto-Assign Strategy" />
          <StrategySelector
            options={ASSIGN_OPTIONS}
            value={config.autoAssignStrategy}
            onChange={(autoAssignStrategy) => updateConfig({ autoAssignStrategy })}
            disabled={disabled}
          />
        </div>
        {config.autoAssignStrategy !== 'none' && (
          <div>
            <FieldLabel label="Team" helper="Select the team to auto-assign incidents to." />
            <select
              className="input"
              value={config.autoAssignTeamId ?? ''}
              onChange={(e) => updateConfig({ autoAssignTeamId: e.target.value || undefined })}
              disabled={disabled || teamsLoading || isSystemDefault}
            >
              {isSystemDefault ? (
                <option value="">— Select team when configuring endpoint —</option>
              ) : (
                <>
                  <option value="">Select team...</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </>
              )}
            </select>
            {isSystemDefault && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Team assignment is tenant-specific and must be configured per endpoint pipeline.
              </p>
            )}
          </div>
        )}
        </div>
      </details>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>🔄 Skip Incident Creation</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{(config.skipCreationForStates ?? []).length} states</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-6">
          <SectionHeader title="Skip Incident Creation" />

        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Skip Incident Creation for States</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            If the normalized event state matches one of these values, incident creation is skipped.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(['Resolved', 'Closed', 'Informational', 'Acknowledged'] as const).map((state) => {
              const selectedStates = config.skipCreationForStates ?? [];
              const isChecked = selectedStates.some((value) => value.toLowerCase() === state.toLowerCase());

              return (
                <label key={state} className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const current = config.skipCreationForStates ?? [];
                      const next = e.target.checked
                        ? [...current, state]
                        : current.filter((value) => value.toLowerCase() !== state.toLowerCase());
                      updateConfig({ skipCreationForStates: next });
                    }}
                    disabled={disabled}
                    className="h-4 w-4 rounded"
                  />
                  {state}
                </label>
              );
            })}
          </div>
        </div>
        </div>
      </details>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>🔄 Auto-Resolve &amp; Reopen</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{autoResolveRules.length + autoReopenRules.length} rules</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Auto-Resolve &amp; Reopen</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
            <span>Resolve Reason</span>
            <select
              className="input"
              value={config.resolveReason ?? 'SolutionImplemented'}
              onChange={(e) => updateConfig({ resolveReason: e.target.value })}
              disabled={disabled}
            >
              {effectiveResolveReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>{reason.label}</option>
              ))}
            </select>
          </label>

          <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
            <span className="block">Resolve Note Template</span>
            <textarea
              className={`input min-h-[88px] ${dragOverTarget === 'resolveNote' ? 'ring-2 ring-blue-400' : ''}`}
              value={config.resolveNoteTemplate ?? ''}
              onChange={(e) => updateConfig({ resolveNoteTemplate: e.target.value })}
              onDragOver={(event) => handleDragOver(event, 'resolveNote')}
              onDragLeave={handleDragLeave}
              onDrop={(event) => {
                handleDropOnResolveNote(event);
                setDragOverTarget(null);
              }}
              disabled={disabled}
              placeholder="Optional template for auto-resolve notes"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Supports placeholders like <code>{'{{monitorCondition}}'}</code> and <code>{'{{normalized.impact}}'}</code>.
            </p>
            <TemplateFunctionsReference />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.enableAutoResolve}
            onChange={(e) => updateConfig({ enableAutoResolve: e.target.checked })}
            disabled={disabled}
            className="h-4 w-4 rounded"
          />
          Enable auto-resolve
        </label>

        {config.enableAutoResolve && (
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Auto-Resolve Rules</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              All matching is case-insensitive. For example, "Resolved" will match "resolved", "RESOLVED", etc.
            </p>
            {autoResolveRules.map((rule, index) => {
              const summarySource = rule.field || '(no source)';
              const summaryPattern = rule.value || '(no pattern)';

              return (
                <details key={rule.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <span className="truncate">📋 #{index + 1} {summarySource}</span>
                    <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{summaryPattern}</span>
                  </summary>
                  <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
                    <div className="ml-auto flex items-center justify-end gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(e) => updateAutoResolveRule(rule.id, { enabled: e.target.checked })}
                          disabled={disabled}
                          className="h-4 w-4 rounded"
                        />
                        Enabled
                      </label>
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        onClick={() => deleteAutoResolveRule(rule.id)}
                        disabled={disabled}
                      >
                        Delete
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>Name</span>
                        <input
                          className="input"
                          value={rule.name}
                          onChange={(e) => updateAutoResolveRule(rule.id, { name: e.target.value })}
                          disabled={disabled}
                          placeholder="Rule name"
                        />
                      </label>

                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>
                          Match Strategy
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">(case-insensitive)</span>
                        </span>
                        <select
                          className="input"
                          value={rule.matchStrategy}
                          onChange={(e) => updateAutoResolveRule(rule.id, { matchStrategy: e.target.value as AutoTransitionRule['matchStrategy'] })}
                          disabled={disabled}
                        >
                          <option value="exact">exact</option>
                          <option value="contains">contains</option>
                          <option value="regex">regex</option>
                        </select>
                      </label>

                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>Field</span>
                        <select
                          className="input"
                          value={isKnownField(rule.field) ? rule.field : '__custom__'}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateAutoResolveRule(rule.id, { field: v === '__custom__' ? '' : v });
                          }}
                          disabled={disabled}
                        >
                          {autoTransitionFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                          <option value="__custom__">Custom JSON path…</option>
                        </select>
                        {!isKnownField(rule.field) && (
                          <input
                            type="text"
                            className="input mt-1"
                            value={rule.field}
                            onChange={(e) => updateAutoResolveRule(rule.id, { field: e.target.value })}
                            placeholder="e.g., data.essentials.monitorCondition"
                            disabled={disabled}
                          />
                        )}
                      </label>

                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>Value</span>
                        <input
                          className="input"
                          value={rule.value}
                          onChange={(e) => updateAutoResolveRule(rule.id, { value: e.target.value })}
                          disabled={disabled}
                          placeholder="Match value"
                        />
                      </label>
                    </div>

                    <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1 block">
                      <span>Resolution Notes (optional)</span>
                      <input
                        className="input"
                        value={rule.resolutionNotes ?? ''}
                        onChange={(e) => updateAutoResolveRule(rule.id, { resolutionNotes: e.target.value })}
                        disabled={disabled}
                        placeholder="Optional notes applied when auto-resolving"
                      />
                    </label>
                  </div>
                </details>
              );
            })}
            <button type="button" className="btn-secondary text-sm" onClick={addAutoResolveRule} disabled={disabled}>
              Add Rule
            </button>
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.enableAutoReopen}
            onChange={(e) => updateConfig({ enableAutoReopen: e.target.checked })}
            disabled={disabled}
            className="h-4 w-4 rounded"
          />
          Enable auto-reopen
        </label>

        {config.enableAutoReopen && (
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Auto-Reopen Rules</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              All matching is case-insensitive. For example, "Resolved" will match "resolved", "RESOLVED", etc.
            </p>
            {autoReopenRules.map((rule, index) => {
              const summarySource = rule.field || '(no source)';
              const summaryPattern = rule.value || '(no pattern)';

              return (
                <details key={rule.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <span className="truncate">📋 #{index + 1} {summarySource}</span>
                    <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{summaryPattern}</span>
                  </summary>
                  <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
                    <div className="ml-auto flex items-center justify-end gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(e) => updateAutoReopenRule(rule.id, { enabled: e.target.checked })}
                          disabled={disabled}
                          className="h-4 w-4 rounded"
                        />
                        Enabled
                      </label>
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        onClick={() => deleteAutoReopenRule(rule.id)}
                        disabled={disabled}
                      >
                        Delete
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>Name</span>
                        <input
                          className="input"
                          value={rule.name}
                          onChange={(e) => updateAutoReopenRule(rule.id, { name: e.target.value })}
                          disabled={disabled}
                          placeholder="Rule name"
                        />
                      </label>

                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>
                          Match Strategy
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">(case-insensitive)</span>
                        </span>
                        <select
                          className="input"
                          value={rule.matchStrategy}
                          onChange={(e) => updateAutoReopenRule(rule.id, { matchStrategy: e.target.value as AutoTransitionRule['matchStrategy'] })}
                          disabled={disabled}
                        >
                          <option value="exact">exact</option>
                          <option value="contains">contains</option>
                          <option value="regex">regex</option>
                        </select>
                      </label>

                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>Field</span>
                        <select
                          className="input"
                          value={isKnownField(rule.field) ? rule.field : '__custom__'}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateAutoReopenRule(rule.id, { field: v === '__custom__' ? '' : v });
                          }}
                          disabled={disabled}
                        >
                          {autoTransitionFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                          <option value="__custom__">Custom JSON path…</option>
                        </select>
                        {!isKnownField(rule.field) && (
                          <input
                            type="text"
                            className="input mt-1"
                            value={rule.field}
                            onChange={(e) => updateAutoReopenRule(rule.id, { field: e.target.value })}
                            placeholder="e.g., data.essentials.monitorCondition"
                            disabled={disabled}
                          />
                        )}
                      </label>

                      <label className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                        <span>Value</span>
                        <input
                          className="input"
                          value={rule.value}
                          onChange={(e) => updateAutoReopenRule(rule.id, { value: e.target.value })}
                          disabled={disabled}
                          placeholder="Match value"
                        />
                      </label>
                    </div>
                  </div>
                </details>
              );
            })}
            <button type="button" className="btn-secondary text-sm" onClick={addAutoReopenRule} disabled={disabled}>
              Add Rule
            </button>
          </div>
        )}
      </div>
      </details>
      </div>
      </div>
      <div className="w-64 flex-shrink-0">
        <div className="sticky top-0">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Available Fields</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Drag a field and drop it into any text input, or click to copy the template token.
          </p>
          <AvailableFieldsPane
            customFields={templateCustomFields}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
